import { ActionIcon, Button, Card, Grid, Group, Stack, Text } from '@mantine/core';
import { IconEye, IconLayoutGrid, IconList, IconPlus, IconSquareArrowRight, IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useContextMenu } from 'mantine-contextmenu';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import type { SerializableRenderTreeNode } from '../../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { formatToPascalCase } from '../../../../../../react-commons/utils/utilFunctions';
import { setWorkflowRepresentationGridView } from '../../../../persisted/global/globalActions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { WorkflowRepresentationType } from '../../../../resource/enumerations/WorkflowRepresentationType';
import {
    defaultWorkflowRepresentationResource,
    useWorkflowRepresentationActions,
    type WorkflowRepresentationResource,
} from '../../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import {
    defaultUseCaseWorkflowAssociationRepresentationResource,
    type UseCaseWorkflowAssociationRepresentationResource,
    useUseCaseWorkflowAssociationRepresentationActions,
} from '../../../../resource/resources/artifacts/use_case/useCaseWorkflowRepresentationAssociationResource';
import { useUserActions } from '../../../../resource/resources/persistables/user/userResource';
import type { RootState } from '../../../../store/rootReducer';
import { useUserId } from '../../../authentication/useUserId';
import { ConditionalComponent } from '../../../reusable-components/ConditionalComponent';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { defaultLoadingNode } from '../../../util/parseTreeFunctions';

type WorkflowRepresentationsStructureComponentTreeNode = SerializableRenderTreeNode<WorkflowRepresentationResource>;

const parseTree = (
    resources: QueryObserverResult<Array<WorkflowRepresentationResource> | null>,
    associations: QueryObserverResult<Array<UseCaseWorkflowAssociationRepresentationResource> | null>
): Array<WorkflowRepresentationsStructureComponentTreeNode> => {
    if (resources.isLoading || associations.isLoading) {
        return [defaultLoadingNode];
    }
    if (!resources.data) {
        return [];
    }

    return resources.data
        .filter((resource) =>
            associations.data?.some((association) => association.workflowRepresentation === resource.objectId)
        )
        .map((resource) => ({
            key: resource.id,
            label: resource.name,
            data: resource,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));
};

export const WorkflowRepresentationsStructureComponent = ({
    projectVersionId,
    useCaseId,
    onOpen,
}: {
    projectVersionId: string | undefined;
    useCaseId: string | undefined;
    onOpen: (id: string) => void;
}) => {
    const { showContextMenu } = useContextMenu();
    const { id: projectId } = useParams<{ id: string }>();
    const userId = useUserId('');
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const dispatch = useDispatch();
    const viewMode = useSelector((state: RootState) =>
        state.persisted.global.workflowRepresentationGridView ? 'grid' : 'list'
    );
    const [selectedNode, setSelectedNode] = React.useState<WorkflowRepresentationsStructureComponentTreeNode | null>(
        null
    );
    const [nodes, setNodes] = React.useState<Array<WorkflowRepresentationsStructureComponentTreeNode>>([]);

    const representationActions = useWorkflowRepresentationActions();
    const useCaseRepresentationAssociationActions = useUseCaseWorkflowAssociationRepresentationActions();

    const representationsQuery = representationActions.useGetAllResources(projectVersionId);
    const representationAssociationsQuery = useCaseRepresentationAssociationActions.useGetAllResources(useCaseId);
    const userActions = useUserActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const addRepresentation = representationActions.useAddResource();
    const addRepresentationAssociation = useCaseRepresentationAssociationActions.useAddResource();
    const deleteRepresentationAssociation = useCaseRepresentationAssociationActions.useDeleteResource();

    const isProcessAdmin = userSystemRights.data?.isProcessAdmin;

    React.useEffect(() => {
        const newNodes = parseTree(representationsQuery, representationAssociationsQuery);
        setNodes(newNodes);
    }, [representationsQuery.data, representationAssociationsQuery.data, projectVersionId]);

    React.useEffect(() => {
        setSelectedNode(null);
    }, [projectVersionId, useCaseId]);

    return (
        <Card shadow={'sm'} padding={'md'} radius={'md'} withBorder style={{ height: '100%' }}>
            <ConditionalComponent
                condition={Boolean(useCaseId && projectVersionId)}
                label={'Select a Use Case to continue'}
            >
                <Stack h={'100%'}>
                    <ResourceValidationTitleBar
                        resourceType={ResourceType.WORKFLOW_REPRESENTATION}
                        customButtons={
                            <Group>
                                <ActionIcon
                                    size={'sm'}
                                    onClick={(event) => {
                                        showContextMenu([
                                            {
                                                key: 'list',
                                                title: 'List',
                                                icon: <IconList />,
                                                onClick: () => dispatch(setWorkflowRepresentationGridView(false)),
                                            },
                                            {
                                                key: 'grid',
                                                title: 'Grid',
                                                icon: <IconLayoutGrid />,
                                                onClick: () => dispatch(setWorkflowRepresentationGridView(true)),
                                            },
                                        ])(event);
                                    }}
                                    variant={'transparent'}
                                    color={'var(--mantine-color-base-button)'}
                                >
                                    <IconEye />
                                </ActionIcon>
                                <Show when={isProcessAdmin}>
                                    <ActionIcon
                                        size={'sm'}
                                        onClick={() =>
                                            OpenModal.SelectionInputModal(
                                                'Workflow Representation Type to Select or Create',
                                                Object.values(WorkflowRepresentationType).map((value) => ({
                                                    label: formatToPascalCase(value),
                                                    value,
                                                })),
                                                false,
                                                'V_CYCLE',
                                                (typeString) => {
                                                    const type =
                                                        typeString === WorkflowRepresentationType.GRID ?
                                                            WorkflowRepresentationType.GRID
                                                        :   WorkflowRepresentationType.V_CYCLE;
                                                    OpenModal.SelectionOrCreateModal(
                                                        'Select or Create a new Workflow Representation',
                                                        representationsQuery.data
                                                            ?.filter((representation) => representation.type === type)
                                                            .filter(
                                                                (representation) =>
                                                                    !representationAssociationsQuery.data?.some(
                                                                        (association) =>
                                                                            association.workflowRepresentation ===
                                                                            representation.objectId
                                                                    )
                                                            )
                                                            .map((representation) => ({
                                                                label: representation.name,
                                                                value: representation.objectId,
                                                            })) ?? [],
                                                        true,
                                                        null,
                                                        (value) => {
                                                            if (value) {
                                                                addRepresentationAssociation.mutate({
                                                                    resource: {
                                                                        ...defaultUseCaseWorkflowAssociationRepresentationResource,
                                                                        workflowRepresentation: value,
                                                                    },
                                                                    parentId: useCaseId,
                                                                });
                                                            }
                                                        },
                                                        (value, multiLineValue) => {
                                                            if (value) {
                                                                addRepresentation.mutate(
                                                                    {
                                                                        resource: {
                                                                            ...defaultWorkflowRepresentationResource,
                                                                            name: value,
                                                                            description: multiLineValue ?? '',
                                                                            type: type,
                                                                        },
                                                                        parentId: projectVersionId,
                                                                    },
                                                                    {
                                                                        onSuccess: (result) => {
                                                                            const representationObjectId =
                                                                                result?.content?.objectId;
                                                                            if (representationObjectId) {
                                                                                addRepresentationAssociation.mutate({
                                                                                    resource: {
                                                                                        ...defaultUseCaseWorkflowAssociationRepresentationResource,
                                                                                        workflowRepresentation:
                                                                                            representationObjectId,
                                                                                    },
                                                                                    parentId: useCaseId,
                                                                                });
                                                                            }
                                                                        },
                                                                    }
                                                                );
                                                            }
                                                        },
                                                        undefined,
                                                        'Name',
                                                        true,
                                                        'Description',
                                                        800
                                                    );
                                                },
                                                undefined,
                                                undefined,
                                                true
                                            )
                                        }
                                        variant={'transparent'}
                                        color={'var(--mantine-color-base-button)'}
                                    >
                                        <IconPlus />
                                    </ActionIcon>
                                </Show>
                            </Group>
                        }
                    >
                        <Text>Workflow Representations ({nodes.length}) of Use Case</Text>
                    </ResourceValidationTitleBar>
                    <Show when={viewMode === 'list'}>
                        <Tree
                            nodes={nodes}
                            rootExpanded
                            selectedNodeKey={selectedNode?.key}
                            onNodeSelected={setSelectedNode}
                            getElements={(node) => ({
                                onDoubleClick: () => onOpen(node.data.id),
                                hoverNode: (
                                    <Group gap={0}>
                                        <TreeHoverButton
                                            Icon={IconSquareArrowRight}
                                            onClick={() => onOpen(node.data.id)}
                                        />
                                        {isProcessAdmin && (
                                            <TreeHoverButton
                                                Icon={IconTrash}
                                                onClick={() => {
                                                    const representation = representationsQuery.data?.find(
                                                        (value) => value.id === node.data.id
                                                    );
                                                    if (representation) {
                                                        const association = representationAssociationsQuery.data?.find(
                                                            (value) =>
                                                                value.workflowRepresentation === representation.objectId
                                                        );
                                                        if (association) {
                                                            deleteResourceDialogFunction.deleteResource(
                                                                association.id,
                                                                node.data.name,
                                                                ResourceType.USE_CASE_WORKFLOW_REPRESENTATION_ASSOCIATION,
                                                                deleteRepresentationAssociation
                                                            );
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                    </Group>
                                ),
                            })}
                        />
                    </Show>
                    <Show when={viewMode === 'grid'}>
                        <Grid>
                            {nodes.map((node) => (
                                <Grid.Col key={node.key} span={{ base: 12, md: 6 }}>
                                    <Button
                                        w={'100%'}
                                        size={'lg'}
                                        rightSection={
                                            <Group gap={0}>
                                                <TreeHoverButton
                                                    Icon={IconSquareArrowRight}
                                                    onClick={() => onOpen(node.data.id)}
                                                />
                                                {isProcessAdmin && (
                                                    <TreeHoverButton
                                                        Icon={IconTrash}
                                                        onClick={() => {
                                                            const representation = representationsQuery.data?.find(
                                                                (value) => value.id === node.data.id
                                                            );
                                                            if (representation) {
                                                                const association =
                                                                    representationAssociationsQuery.data?.find(
                                                                        (value) =>
                                                                            value.workflowRepresentation ===
                                                                            representation.objectId
                                                                    );
                                                                if (association) {
                                                                    deleteResourceDialogFunction.deleteResource(
                                                                        association.id,
                                                                        node.data.name,
                                                                        ResourceType.USE_CASE_WORKFLOW_REPRESENTATION_ASSOCIATION,
                                                                        deleteRepresentationAssociation
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Group>
                                        }
                                        justify="space-between"
                                        style={
                                            selectedNode?.key === node.key ?
                                                { border: '2px solid var(--mantine-text-secondary-color)' }
                                            :   undefined
                                        }
                                        onClick={() => {
                                            setSelectedNode(node);
                                            onOpen(node.key);
                                        }}
                                    >
                                        {node.label}
                                    </Button>
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Show>
                </Stack>
            </ConditionalComponent>
        </Card>
    );
};
